/**
 * 聚合计数器（P0-14）
 *
 * 背景：第三章「错误优先」的降噪铁律是「正常态一律不上报」，但这样会把算「率」
 * 所需的分母也一并砍掉 —— 画布保存失败率 / SSE 断流率 / 轮询耗尽率这三条告警，
 * 若只上报失败次数、没有总次数，实际配不出来（见方案 C.3）。
 *
 * 做法：正常态操作只 bump() 内存计数，不逐次上报；每分钟聚合成一条 op_total
 * 事件送出。代价约 1440 条/天（单事件名，用 op 维度区分），换来三条 P0 告警
 * 可精确配置。
 *
 * 调用方在 P1/P2 落地画布持久化与 AI 流式探针时接入，此处先提供基础设施。
 */

import apmClient from '@apmplus/web';
import { getCurrentFeature, normalizeModelName, tierOf } from './context';

const FLUSH_INTERVAL_MS = 60_000;

const counters: Record<string, number> = {};
// 带模型维度的计数：key = `${op}|${modelName}`（modelName 已归一化，低基数）
const modelCounters: Record<string, { count: number; modelName: string }> = {};
let flushTimer: ReturnType<typeof setInterval> | null = null;

/** 常态调用：仅计数，不产生任何网络请求 */
export function bump(op: string, count = 1): void {
  counters[op] = (counters[op] ?? 0) + count;
}

/**
 * 带模型维度的计数（模型选择 / 提交成败等）
 *
 * 维度值一律走 normalizeModelName（禁止 ULID 进 categories，见 context.ts 规范），
 * 同时带上 model_tier 便于长尾模型被挤出看板时兜底筛选。
 */
export function bumpModel(op: string, rawModelName: string | undefined | null, count = 1): void {
  const modelName = normalizeModelName(rawModelName);
  const key = `${op}|${modelName}`;
  if (!modelCounters[key]) {
    modelCounters[key] = { count: 0, modelName };
  }
  modelCounters[key].count += count;
}

function flushCounters(): void {
  for (const [op, count] of Object.entries(counters)) {
    if (count === 0) continue;
    try {
      apmClient.sendEvent?.({
        name: 'op_total',
        metrics: { count },
        categories: { op, feature: getCurrentFeature() },
      });
    } catch {
      // 忽略单次上报失败，不影响下一轮计数
    }
    counters[op] = 0;
  }

  for (const [key, entry] of Object.entries(modelCounters)) {
    if (entry.count === 0) continue;
    const op = key.split('|')[0];
    try {
      apmClient.sendEvent?.({
        name: 'op_total',
        metrics: { count: entry.count },
        categories: {
          op,
          model_name: entry.modelName,
          model_tier: tierOf(entry.modelName),
          feature: getCurrentFeature(),
        },
      });
    } catch {
      // 忽略单次上报失败，不影响下一轮计数
    }
    entry.count = 0;
  }
}

/** 应用启动时调用一次，开启每分钟聚合上报 */
export function startCounterFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setInterval(flushCounters, FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', flushCounters); // 兜底，防丢尾
}
